import type { TranscriptPayload } from '@Application/Beneficiaries/Transcripts/Actions/transcriptFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `interviewId` is derived from `payload.interview.id`, never from a sibling action field: it is the
 * only source that describes the transcript itself rather than the call context. `engineFamily` is an
 * enum, wrapped in `LabeledValueView`, never read as a bare string.
 *
 * A transcript only ever exists once it has succeeded: the server never emits `status`,
 * `failureReason`, `failureMessage`, `attemptCount`, `requestedAt`, `startedAt` or `completedAt` on
 * this route, and `GET …/transcript` answers `404` for every state that precedes success — a caller
 * reads "no transcript yet" from that `404`, never from a status field on this View. `reviewedAt`
 * follows the same nullable-date conversion `RecordingView.completedAt` uses. `reviewedBy` is kept as
 * a bare `{ id }` guard, never resolved to a name: the screen has no user collection loaded to resolve
 * it against, and displays "Relu le {date}", never "par {name}".
 */
class TranscriptView {
	readonly id: number;
	readonly interviewId: number;
	readonly engineFamily: LabeledValueView | null;
	readonly turnCount: number;
	readonly hasSpeakers: boolean;
	readonly reviewedAt: Date | null;
	readonly reviewedById: number | null;
	readonly revisedTurnCount: number;

	constructor(payload: TranscriptPayload) {
		this.id = payload.id;
		this.interviewId = payload.interview.id;
		this.engineFamily = payload.engineFamily !== null ? new LabeledValueView(payload.engineFamily) : null;
		this.turnCount = payload.turnCount;
		this.hasSpeakers = payload.hasSpeakers;
		this.reviewedAt = payload.reviewedAt !== null ? new Date(payload.reviewedAt) : null;
		this.reviewedById = payload.reviewedBy?.id ?? null;
		this.revisedTurnCount = payload.revisedTurnCount;

		Object.freeze(this);
	}

	get isReviewed(): boolean {
		return this.reviewedAt !== null;
	}
}

export default TranscriptView;
